import i18n from '../i18n';
import * as bootstrap from 'bootstrap';
import * as models from 'smg/admin/models';
import axios from 'smg/axios';
import * as countries from 'smg/countries';
import { ShowError } from 'smg/errors';
import * as template from 'smg/template';
import * as uploader from 'smg/uploader/v2';
import { RenderUsageMeta } from './cardusage';

export interface TaxExemptionHandle {
    // Repopulate the picker for a new customer. Pass an empty string to clear
    // (e.g. when the email/customer is cleared in the contact section).
    refresh(userID: string): void;
    // Track the current shipping destination so we can gate whether the
    // configured exemption is actually applicable. The exemption must be for
    // the same country, and either match the destination state or be "all
    // states" within that country. Backend re-validates at order create —
    // this just keeps the summary total honest.
    setShippingDestination(addrInit: models.IAddress | undefined): void;
    // Snapshot of the exemption to attach to the order at place-order time,
    // or undefined when there's nothing applicable to attach (skip checked,
    // form incomplete, or the exemption doesn't match the shipping
    // destination — same gate as the summary's exempt verdict so the
    // displayed tax and the submitted exemption never disagree). Mirrors
    // shipping.snapshot(): an existing pick rides as a bare
    // userTaxExemptionID; a newly-authored exemption rides as an inline
    // userTaxExemption submodel (reason/number/state + cert uploads) that
    // the server's UserTaxExemptionOrder.Normalize materializes. Async because
    // the cert uploader's uploadIDs() now blocks on any in-flight S3 PUTs so we
    // never submit an upload ID whose PUT hasn't landed.
    snapshot(): Promise<models.UserTaxExemptionOrder | undefined>;
}

export interface TaxExemptionDeps {
    // Fires whenever the order's exempt state changes: true means "treat this
    // order as exempt — zero the tax in the summary"; false means "normal
    // tax." Drives the summary total only — the exemption that actually rides
    // the order POST comes from snapshot().
    onExemptChange?: (exempt: boolean) => void;
    // Returns the realm/company the new exemption should belong to. Used by
    // the uploader to scope the upload to the right CompanyID bucket. The
    // realm lives in the contact section, so the page wires this through
    // rather than reaching into contact's DOM from here.
    getCompanyID?: () => string | undefined;
}

// Reason labels match the database `usertaxexemptionreasons.Name` values so
// admin and customer-facing copy stay aligned. Hardcoded because the enum
// values are stable and fetching the list would just be a roundtrip to read
// strings we already know.
const reasonOptions: { id: models.UserTaxExemptionReasonID; label: string }[] = [
    { id: models.UserTaxExemptionReasonID.USER_TAX_EXEMPTION_REASON_ENTITY_EXEMPT, label: i18n('taxExemptEntity') },
    { id: models.UserTaxExemptionReasonID.USER_TAX_EXEMPTION_REASON_GOVERNMENT_EXEMPTION, label: i18n('taxExemptGovernment') },
    { id: models.UserTaxExemptionReasonID.USER_TAX_EXEMPTION_REASON_WHOLESALE_OR_RETAIL_EXEMPTION, label: i18n('taxExemptWholesale') },
];

export function SetupTaxExemption(deps?: TaxExemptionDeps): TaxExemptionHandle {
    const $skip = document.getElementById('tax-exempt-skip') as HTMLInputElement;
    const $collapse = document.getElementById('tax-exempt-form-collapse') as HTMLElement;
    const $fieldset = document.getElementById('tax-exempt-fieldset') as HTMLFieldSetElement;
    const $optionsRow = document.getElementById('tax-exempt-options-row') as HTMLElement;
    const $optionsScroller = document.getElementById('tax-exempt-options-scroller') as HTMLElement;
    const $stateWarning = document.getElementById('tax-exempt-state-warning') as HTMLElement;
    const $stateWarningBody = document.getElementById('tax-exempt-state-warning-body') as HTMLElement;

    const $reason = document.getElementById('tax-exempt-reason') as HTMLSelectElement;
    const $number = document.getElementById('tax-exempt-number') as HTMLInputElement;
    const $state = document.getElementById('tax-exempt-state') as HTMLSelectElement;
    const $sectionDropZone = document.getElementById('tax-exempt-dropzone') as HTMLElement;
    const $uploadDropZone = document.getElementById('tax-exempt-upload-dropzone') as HTMLElement;
    const $uploadInput = document.getElementById('tax-exempt-upload-input') as HTMLInputElement;
    const $uploadPreviews = document.getElementById('tax-exempt-upload-previews') as HTMLElement;
    const $clearBtn = document.getElementById('tax-exempt-clear') as HTMLButtonElement;

    // Drive ReportValidity for the certificate upload via setCustomValidity:
    // the file input is visually hidden inside the dropzone label
    // (`.validity-parent`), so when invalid the helper walks up to the label
    // and applies `.is-invalid` to the visible dropzone. Plus/v2 uses the
    // same pattern in checkout/tax.ts.
    const certEmptyMessage = 'Attach an exemption certificate.';
    function syncUploadValidity() {
        $uploadInput.setCustomValidity(certUploader.uploads().length > 0 ? '' : certEmptyMessage);
        if (certUploader.uploads().length > 0) {
            $uploadInput.dispatchEvent(new Event('clearinvalid'));
        }
    }

    for (const r of reasonOptions) {
        const $opt = document.createElement('option');
        $opt.value = r.id.toString();
        $opt.textContent = r.label;
        $reason.appendChild($opt);
    }

    void countries.StateSelect(
        models.CountryID.COUNTRIES_UNITED_STATES,
        $state,
        { stateParentSelector: '.state-field-wrapper' },
    ).catch(ShowError);

    const certUploader = new uploader.Uploader({
        $input: $uploadInput,
        $dropzones: [$sectionDropZone, $uploadDropZone],
        $thumbnailsParent: $uploadPreviews,
        companyID: () => Promise.resolve(deps?.getCompanyID?.() ?? ''),
        onComplete: () => {
            syncUploadValidity();
            notifyChange();
        },
        // Thumbnail delete and the undo-restore both change isConfigured()'s
        // result — without re-notifying, the summary could stay tax-exempt
        // after the admin removes the only cert.
        onRemove: () => {
            syncUploadValidity();
            notifyChange();
        },
        onRestore: () => {
            syncUploadValidity();
            notifyChange();
        },
    });
    // Initial state: no uploads → ReportValidity will flag the dropzone.
    syncUploadValidity();

    let currentUserID = '';
    let selectedExistingID: string | undefined;
    let $activeCard: HTMLElement | undefined;
    let shippingStateID = 0;
    let shippingCountryID = 0;
    let exemptions: models.HyejooUserTaxExemption[] = [];

    function clearActiveCard() {
        $activeCard?.classList.remove('active');
        $activeCard = undefined;
    }

    function formStateID(): number {
        return parseInt($state.value, 10) || 0;
    }

    function isConfigured(): boolean {
        // Either an existing exemption is picked, or the form has the minimum
        // fields a new exemption needs (reason + number + state + at least one
        // upload). State is required — admin must explicitly pick a specific
        // state or "All states" (value "0") rather than leave the placeholder.
        if (selectedExistingID) {
            return true;
        }
        return $reason.value !== '' && $number.value.trim() !== '' && $state.value !== '' && certUploader.uploads().length > 0;
    }

    function updateStateWarning(applicable: boolean) {
        if (applicable) {
            $stateWarning.classList.add('d-none');
            return;
        }
        if (shippingCountryID !== models.CountryID.COUNTRIES_UNITED_STATES) {
            $stateWarningBody.textContent =
                i18n('taxExemptOutsideUS');
        } else {
            // Country matches but the exemption is tied to a specific state
            // that isn't the destination (an "All states" exemption applies
            // country-wide and never lands here — isApplicable returns true
            // for stateID 0).
            const exemptState = countries.GetStateSync(models.CountryID.COUNTRIES_UNITED_STATES, formStateID());
            const shipState = shippingStateID > 0 ?
                countries.GetStateSync(models.CountryID.COUNTRIES_UNITED_STATES, shippingStateID) :
                undefined;
            const exemptName = exemptState?.name ?? 'a different state';
            const shipName = shipState?.name ?? 'the shipping destination';
            $stateWarningBody.textContent =
                i18n('taxExemptWrongState').replace('%s', exemptName).replace('%s', shipName);
        }
        $stateWarning.classList.remove('d-none');
    }

    // Does the configured exemption apply to the current shipping
    // destination? Mirrors the server guard in
    // UserTaxExemptionOrder.Normalize (go/v2/usertaxexemptionorders.go): the
    // form is US-only, so a non-US (or not-yet-known) destination
    // disqualifies; a stateID of 0 ("All states") applies country-wide,
    // otherwise the exemption's state must equal the destination's. Pure —
    // notifyChange owns the warning UI, snapshot() owns the order payload.
    function isApplicable(): boolean {
        if (shippingCountryID !== models.CountryID.COUNTRIES_UNITED_STATES) {
            return false;
        }
        const stateID = formStateID();
        return stateID === 0 || stateID === shippingStateID;
    }

    function notifyChange() {
        if ($skip.checked || !isConfigured()) {
            $stateWarning.classList.add('d-none');
            deps?.onExemptChange?.(false);
            return;
        }
        // Shipping address isn't picked yet — can't determine applicability,
        // so don't apply OR warn. The admin will see the verdict once they
        // finish the address.
        if (shippingCountryID === 0) {
            $stateWarning.classList.add('d-none');
            deps?.onExemptChange?.(false);
            return;
        }
        const applied = isApplicable();
        updateStateWarning(applied);
        deps?.onExemptChange?.(applied);
    }

    function fillFormFromExemption(ute: models.HyejooUserTaxExemption) {
        $reason.value = (ute.userTaxExemptionReasonID ?? ute.userTaxExemptionReason?.id ?? '').toString();
        $number.value = ute.number ?? '';
        // stateID 0 maps to the "All states" option (also value "0"), not the
        // placeholder (value ""). Existing exemptions always have an explicit
        // state intent, so we never want to leave the dropdown unselected.
        const exemptStateID = ute.stateID || (ute.state?.id ?? 0);
        $state.value = exemptStateID.toString();
        // Programmatic .value = doesn't trigger input/change, so any
        // is-invalid markers from a prior place-order attempt would linger.
        // Drop them now that the form is populated.
        for (const $el of [$reason, $number, $state]) {
            $el.dispatchEvent(new Event('clearinvalid'));
        }
        certUploader.reset();
        // Existing certs are already uploaded server-side — add them as
        // completed uploads (no upload step) so the admin sees what's
        // attached without us re-POSTing /uploads.
        const existing: uploader.Upload[] = [];
        for (const u of ute.userTaxExemptionUploads) {
            const upl = u.upload;
            if (!upl?.id) {
                continue;
            }
            existing.push({
                id: upl.id,
                name: upl.name,
                mime: upl.mime,
                key: upl.key,
            });
        }
        // addUploads is async — it awaits thumbnail rendering before pushing
        // each entry onto its internal list — so syncUploadValidity has to
        // wait for it. Calling it inline would observe an empty uploads
        // array and leave a stale "Attach an exemption certificate." flagged
        // even after the cert appears in the thumbnails row.
        void certUploader.addUploads(existing).then(syncUploadValidity);
    }

    function selectExistingExemption(ute: models.HyejooUserTaxExemption, $card: HTMLElement) {
        if (!ute.id) {
            return;
        }
        selectedExistingID = ute.id;
        clearActiveCard();
        $card.classList.add('active');
        $activeCard = $card;
        fillFormFromExemption(ute);
        notifyChange();
    }

    function clearForm() {
        $reason.value = '';
        $number.value = '';
        $state.value = '';
        certUploader.reset();
        syncUploadValidity();
        selectedExistingID = undefined;
        clearActiveCard();
        notifyChange();
    }

    function renderOptions() {
        if (exemptions.length === 0) {
            $optionsScroller.replaceChildren();
            $optionsRow.classList.add('d-none');
            return;
        }

        const f = document.createDocumentFragment();
        for (const ute of exemptions) {
            if (!ute.id) {
                continue;
            }
            const $card = template.Clone('#tax-exempt-card-template').firstElementChild as HTMLElement;
            $card.dataset.id = ute.id;

            const $reasonEl = $card.querySelector('.tax-exempt-card-reason') as HTMLElement;
            const $numberEl = $card.querySelector('.tax-exempt-card-number') as HTMLElement;
            const $stateEl = $card.querySelector('.tax-exempt-card-state') as HTMLElement;
            const $metaEl = $card.querySelector('.tax-exempt-card-meta') as HTMLElement;
            const $defaultBadge = $card.querySelector('.tax-exempt-card-default-badge') as HTMLElement;

            $reasonEl.textContent = ute.userTaxExemptionReason?.name ?? 'Exemption';
            $numberEl.textContent = `#${ute.number ?? ''}`;
            $stateEl.textContent = i18n('validIn').replace('%s', ute.state?.name ?? i18n('allStates'));
            $defaultBadge.classList.toggle('d-none', !ute.default);
            RenderUsageMeta($metaEl, ute.orders);

            $card.addEventListener('click', () => {
                selectExistingExemption(ute, $card);
            });
            $card.addEventListener('keydown', e => {
                if (e.target !== $card) {
                    return;
                }
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    selectExistingExemption(ute, $card);
                }
            });

            f.appendChild($card);
        }
        $optionsScroller.replaceChildren(f);
        $optionsRow.classList.remove('d-none');
    }

    async function fetchExemptions(userID: string) {
        exemptions = [];
        renderOptions();
        if (!userID) {
            return;
        }
        try {
            const resp = await axios.get('/v2/user-tax-exemptions', {
                params: {
                    // has:uploads filters out exemptions without a
                    // certificate — the cert is required at order time so
                    // unattached exemptions would just fail later.
                    'q': `user:${userID} active:true has:uploads`,
                    'data': 'id,number,userTaxExemptionReasonID,userTaxExemptionReason,state,userTaxExemptionUploads,orders,default',
                    // default desc surfaces the user-picked default exemption
                    // first (Label='Default'); lastOrdered desc tiebreaks by
                    // most-recent use so the next-most-likely option follows.
                    'sort': 'default desc,lastOrdered desc',
                    'max': 50,
                },
            });
            // Re-check currentUserID after the await — a fast customer switch
            // could have moved past this user before the response landed.
            if (currentUserID !== userID) {
                return;
            }
            exemptions = models.HyejooUserTaxExemptionsResponse.fromJSON(resp.data).results;
            renderOptions();
        } catch (err) {
            ShowError(err);
        }
    }

    // Form input clears the existing-exemption pick — typing means the admin
    // wants to author a new exemption, not reuse the picked one.
    for (const $el of [$reason, $state]) {
        $el.addEventListener('change', () => {
            selectedExistingID = undefined;
            clearActiveCard();
            notifyChange();
        });
    }
    $number.addEventListener('input', () => {
        selectedExistingID = undefined;
        clearActiveCard();
        notifyChange();
    });

    $clearBtn.addEventListener('click', () => clearForm());

    async function snapshot(): Promise<models.UserTaxExemptionOrder | undefined> {
        // Same gate as notifyChange's exempt verdict so the displayed tax and
        // the submitted exemption never disagree — nothing rides the order
        // unless the exemption is complete and applies to the destination.
        if ($skip.checked || !isConfigured() || !isApplicable()) {
            return undefined;
        }
        // Existing pick: link the canonical row by ID.
        // UserTaxExemptionOrder.Normalize drops any submodel when an ID is
        // present, so we send just the ID — mirrors shipping's picked-card
        // path.
        if (selectedExistingID) {
            return new models.UserTaxExemptionOrder({ userTaxExemptionID: selectedExistingID });
        }
        // Newly-authored exemption: ride the inline submodel so a single
        // POST /orders materializes the exemption + its cert uploads and
        // links it to the order in one transaction. userID is omitted on
        // purpose — the server derives it from the order's customer so a
        // payload can't mint an exemption for one user and attach it to
        // another's order. countryID is always US (the form is US-only).
        return new models.UserTaxExemptionOrder({
            userTaxExemption: {
                userTaxExemptionReasonID: parseInt($reason.value, 10),
                countryID: models.CountryID.COUNTRIES_UNITED_STATES,
                stateID: formStateID(),
                number: $number.value.trim(),
                userTaxExemptionUploads: (await certUploader.uploadIDs()).map(uploadID => ({ uploadID })),
            },
        });
    }

    const collapse = bootstrap.Collapse.getOrCreateInstance($collapse, { toggle: false });
    $skip.addEventListener('change', () => {
        $fieldset.disabled = $skip.checked;
        if ($skip.checked) {
            collapse.hide();
            $optionsRow.classList.add('d-none');
        } else {
            collapse.show();
            if (exemptions.length > 0) {
                $optionsRow.classList.remove('d-none');
            }
        }
        notifyChange();
    });

    return {
        refresh: userID => {
            currentUserID = userID;
            // Don't wipe form fields or uploads — those are admin-authored
            // draft work and customer-pill switches shouldn't destroy them.
            // The selected-card highlight belongs to the previous customer
            // though, so drop it and let notifyChange recompute applicability
            // against the new shipping context.
            selectedExistingID = undefined;
            clearActiveCard();
            notifyChange();
            void fetchExemptions(userID);
        },
        setShippingDestination: addrInit => {
            const addr = addrInit ? new models.Address(addrInit) : undefined;
            const stateID = addr?.stateID ?? 0;
            const countryID = addr?.countryID ?? 0;
            if (shippingStateID === stateID && shippingCountryID === countryID) {
                return;
            }
            shippingStateID = stateID;
            shippingCountryID = countryID;
            notifyChange();
        },
        snapshot,
    };
}
